import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Loader2, Droplets, Wind, ArrowUp, ArrowDown } from "lucide-react";
import { WeatherIcon } from "./WeatherIcon";
import { getWeather } from "../lib/api";
import { temp, tempUnit, speed, speedUnit, aqiInfo, conditionTheme } from "../lib/weather";

const locKey = (l) => `${l.lat.toFixed(2)},${l.lon.toFixed(2)}`;

export const CompareDialog = ({ open, onOpenChange, current, favorites, units }) => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const seen = new Set();
    const list = [current, ...favorites].filter((l) => {
      const k = locKey(l);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    setLoading(true);
    Promise.all(
      list.map((l) =>
        getWeather(l)
          .then((d) => ({ loc: l, data: d }))
          .catch(() => null)
      )
    ).then((res) => {
      setCards(res.filter(Boolean));
      setLoading(false);
    });
  }, [open, current, favorites]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl glass border-border/60 max-h-[85vh] overflow-y-auto no-scrollbar"
        data-testid="compare-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-extrabold">Bandingkan Kota</DialogTitle>
          <DialogDescription>
            Perbandingan cuaca terkini kota aktif dan kota favorit Anda.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="grid place-items-center py-20 text-muted-foreground">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}

        {!loading && cards.length <= 1 && (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="compare-empty">
            Simpan beberapa kota ke favorit (ikon bintang) untuk membandingkannya di sini.
          </p>
        )}

        {!loading && cards.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-2">
            {cards.map(({ loc, data }) => {
              const c = data.current;
              const th = conditionTheme(c.condition.main, c.condition.icon);
              const aqi = data.aqi ? aqiInfo(data.aqi.aqi) : null;
              return (
                <div
                  key={locKey(loc)}
                  data-testid={`compare-card-${loc.name}`}
                  className="rounded-2xl p-4 border border-border/60 bg-[hsl(var(--surface)/0.6)]"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold">{loc.name}</div>
                      <div className="text-xs text-muted-foreground">{loc.country}</div>
                    </div>
                    <WeatherIcon icon={c.condition.icon} className="w-10 h-10" style={{ color: th.accent }} />
                  </div>
                  <div className="mt-2 flex items-end gap-2">
                    <span className="text-4xl font-black num">
                      {temp(c.temp, units)}
                      <span className="text-lg text-muted-foreground">{tempUnit(units)}</span>
                    </span>
                  </div>
                  <div className="text-xs capitalize text-muted-foreground">{c.condition.description}</div>
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-xs num">
                    <span className="flex items-center gap-1 text-rose-500">
                      <ArrowUp className="w-3.5 h-3.5" />
                      {temp(c.temp_max, units)}°
                    </span>
                    <span className="flex items-center gap-1 text-sky-500">
                      <ArrowDown className="w-3.5 h-3.5" />
                      {temp(c.temp_min, units)}°
                    </span>
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <Droplets className="w-3.5 h-3.5" />
                      {c.humidity}%
                    </span>
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <Wind className="w-3.5 h-3.5" />
                      {speed(c.wind_speed, units)} {speedUnit(units)}
                    </span>
                  </div>
                  {aqi && (
                    <div className="mt-3 flex items-center gap-2 text-xs">
                      <span
                        className="px-2 py-0.5 rounded-full font-semibold"
                        style={{ background: `${aqi.color}22`, color: aqi.color }}
                      >
                        AQI {data.aqi.aqi} · {aqi.label}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
